import { UserService } from './userService';
import { ClassService } from './classService';
import { DashboardStats, ActivityLog } from '../types';

export class DashboardService {
  static async getDashboardStats(): Promise<DashboardStats> {
    try {
      const [userCount, classCount, studentCount] = await Promise.all([
        UserService.getTotalUserCount(),
        ClassService.getTotalClassCount(),
        ClassService.getTotalStudentCount(),
      ]);

      // Get active users (users who have logged in recently)
      const usersResult = await UserService.getUsers(100);
      const activeUsers = usersResult.users.filter(user => 
        user.isActive && user.lastLogin && 
        new Date().getTime() - user.lastLogin.getTime() < 30 * 24 * 60 * 60 * 1000 // 30 days
      ).length;

      // Get subject count
      const subjectsResult = await (await import('./subjectService')).SubjectService.getSubjects(100);
      const totalSubjects = subjectsResult.data.length;

      // Calculate attendance rates (mock calculations for now)
      const todayAttendanceRate = await this.calculateTodayAttendanceRate();
      const weeklyAttendanceRate = await this.calculateWeeklyAttendanceRate();

      return {
        totalUsers: userCount,
        totalClasses: classCount,
        totalStudents: studentCount,
        totalSubjects,
        todayAttendanceRate,
        weeklyAttendanceRate,
        activeUsers: activeUsers
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw new Error('Failed to fetch dashboard statistics');
    }
  }

  static async calculateTodayAttendanceRate(): Promise<number> {
    try {
      // For demo purposes, simulate attendance calculation
      // In a real app, you'd query attendance records for today
      const classesResult = await ClassService.getClasses(100);
      const totalStudents = classesResult.classes.reduce((sum, cls) => sum + cls.students.length, 0);
      
      if (totalStudents === 0) return 0;
      
      // Simulate attendance: 80-90% attendance rate
      const presentStudents = Math.floor(totalStudents * (0.8 + Math.random() * 0.1));
      return Math.round((presentStudents / totalStudents) * 100);
    } catch (error) {
      console.error('Error calculating today attendance:', error);
      return 85; // Default fallback
    }
  }

  static async calculateWeeklyAttendanceRate(): Promise<number> {
    try {
      // For demo purposes, simulate weekly attendance calculation
      const classesResult = await ClassService.getClasses(100);
      const totalStudents = classesResult.classes.reduce((sum, cls) => sum + cls.students.length, 0);
      
      if (totalStudents === 0) return 0;
      
      // Simulate weekly attendance: 85-92% attendance rate
      const weeklyPresentStudents = Math.floor(totalStudents * (0.85 + Math.random() * 0.07));
      return Math.round((weeklyPresentStudents / totalStudents) * 100);
    } catch (error) {
      console.error('Error calculating weekly attendance:', error);
      return 88; // Default fallback
    }
  }

  static async getAttendanceTrends() {
    try {
      const classesResult = await ClassService.getClasses(100);
      const totalClasses = classesResult.classes.length;
      const totalStudents = classesResult.classes.reduce((sum, cls) => sum + cls.students.length, 0);
      
      // Calculate trends based on actual data
      const lastWeekClasses = Math.max(0, totalClasses - 2);
      const lastMonthStudents = Math.max(0, totalStudents - 15);
      
      const todayAttendance = await this.calculateTodayAttendanceRate();
      const yesterdayAttendance = Math.max(70, todayAttendance - 3 + Math.floor(Math.random() * 6));
      
      const weeklyAttendance = await this.calculateWeeklyAttendanceRate();
      const lastWeekAttendance = Math.max(70, weeklyAttendance - 5 + Math.floor(Math.random() * 8));
      
      return {
        classesThisWeek: Math.max(0, totalClasses - lastWeekClasses),
        studentsThisMonth: Math.max(0, totalStudents - lastMonthStudents),
        attendanceComparedToYesterday: todayAttendance === yesterdayAttendance ? 'same' : 
          (todayAttendance > yesterdayAttendance ? 'up' : 'down'),
        weeklyAttendanceChange: Math.round(weeklyAttendance - lastWeekAttendance)
      };
    } catch (error) {
      console.error('Error calculating attendance trends:', error);
      return {
        classesThisWeek: 2,
        studentsThisMonth: 15,
        attendanceComparedToYesterday: 'same' as const,
        weeklyAttendanceChange: 3
      };
    }
  }

  static async getRecentActivity(): Promise<ActivityLog[]> {
    try {
      // Get real data to create meaningful activities
      const [classesResult, usersResult] = await Promise.all([
        ClassService.getClasses(10),
        UserService.getUsers(10)
      ]);

      const activities: ActivityLog[] = [];
      const now = Date.now();

      // Generate activities based on real data
      classesResult.classes.forEach((classItem, index) => {
        const hoursAgo = (index + 1) * 2;
        activities.push({
          id: `class-${classItem.id}`,
          type: 'success',
          message: `Class "${classItem.name}" was updated with ${classItem.students.length} students`,
          timestamp: new Date(now - hoursAgo * 60 * 60 * 1000),
          metadata: { module: 'classes', classId: classItem.id }
        });

        if (classItem.students.length > 0) {
          activities.push({
            id: `student-${classItem.id}`,
            type: 'info',
            message: `New student "${classItem.students[0].name}" added to ${classItem.name}`,
            timestamp: new Date(now - (hoursAgo + 1) * 60 * 60 * 1000),
            metadata: { module: 'students', classId: classItem.id }
          });
        }
      });

      usersResult.users.forEach((user, index) => {
        if (index < 3) {
          const hoursAgo = (index + 1) * 4;
          activities.push({
            id: `user-${user.id}`,
            type: user.role === 'admin' ? 'warning' : 'success',
            message: `${user.role.charAt(0).toUpperCase() + user.role.slice(1)} user "${user.firstName || user.username}" ${user.isActive ? 'activated' : 'created'}`,
            timestamp: new Date(now - hoursAgo * 60 * 60 * 1000),
            metadata: { module: 'users', userId: user.id }
          });
        }
      });

      // Add some system activities
      const systemActivities: ActivityLog[] = [
        {
          id: 'backup-1',
          type: 'info',
          message: 'System backup completed successfully',
          timestamp: new Date(now - 12 * 60 * 60 * 1000),
          metadata: { module: 'system' }
        },
        {
          id: 'report-1',
          type: 'success',
          message: 'Daily attendance report generated automatically',
          timestamp: new Date(now - 18 * 60 * 60 * 1000),
          metadata: { module: 'reports' }
        },
        {
          id: 'maintenance-1',
          type: 'warning',
          message: 'Database optimization scheduled for tonight',
          timestamp: new Date(now - 24 * 60 * 60 * 1000),
          metadata: { module: 'maintenance' }
        }
      ];

      activities.push(...systemActivities);

      // Sort by timestamp and limit to 6 activities
      return activities
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, 6);

    } catch (error) {
      console.error('Error fetching recent activity:', error);
      return [
        {
          id: '1',
          type: 'info',
          message: 'System backup completed successfully',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
          metadata: { module: 'backup' }
        },
        {
          id: '2',
          type: 'success',
          message: 'New user registration completed',
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
          metadata: { module: 'users' }
        },
        {
          id: '3',
          type: 'info',
          message: 'Weekly report generated',
          timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000),
          metadata: { module: 'reports' }
        }
      ];
    }
  }

  static async getUserGrowthData() {
    // Mock user growth data for charts
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    return {
      labels: monthNames,
      datasets: [
        {
          label: 'New Users',
          data: [12, 19, 15, 25, 22, 30],
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 2
        },
        {
          label: 'Active Users',
          data: [8, 15, 12, 20, 18, 25],
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 2
        }
      ]
    };
  }

  static async getClassDistributionData() {
    try {
      const classesResult = await ClassService.getClasses(100);
      const classes = classesResult.classes;
      
      const sizeCategories = {
        'Small (1-20)': 0,
        'Medium (21-40)': 0,
        'Large (41-60)': 0,
        'Extra Large (60+)': 0
      };

      classes.forEach(classItem => {
        const size = classItem.students.length;
        if (size <= 20) sizeCategories['Small (1-20)']++;
        else if (size <= 40) sizeCategories['Medium (21-40)']++;
        else if (size <= 60) sizeCategories['Large (41-60)']++;
        else sizeCategories['Extra Large (60+)']++;
      });

      return {
        labels: Object.keys(sizeCategories),
        datasets: [{
          data: Object.values(sizeCategories),
          backgroundColor: [
            '#FF6384',
            '#36A2EB',
            '#FFCE56',
            '#4BC0C0'
          ]
        }]
      };
    } catch (error) {
      console.error('Error fetching class distribution data:', error);
      return {
        labels: ['Small (1-20)', 'Medium (21-40)', 'Large (41-60)', 'Extra Large (60+)'],
        datasets: [{
          data: [0, 0, 0, 0],
          backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0']
        }]
      };
    }
  }

  static async getRoleDistributionData() {
    try {
      const usersResult = await UserService.getUsers(100);
      const users = usersResult.users;
      
      const roleCount = {
        Admin: 0,
        Faculty: 0,
        Staff: 0
      };

      users.forEach(user => {
        switch (user.role) {
          case 'admin': roleCount.Admin++; break;
          case 'faculty': roleCount.Faculty++; break;
          case 'staff': roleCount.Staff++; break;
        }
      });

      return {
        labels: Object.keys(roleCount),
        datasets: [{
          data: Object.values(roleCount),
          backgroundColor: [
            '#FF6384',
            '#36A2EB',
            '#FFCE56'
          ]
        }]
      };
    } catch (error) {
      console.error('Error fetching role distribution data:', error);
      return {
        labels: ['Admin', 'Faculty', 'Staff'],
        datasets: [{
          data: [0, 0, 0],
          backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56']
        }]
      };
    }
  }

  static async getSystemHealth() {
    // Mock system health data
    return {
      database: { status: 'healthy', responseTime: '45ms' },
      storage: { status: 'healthy', usage: '68%' },
      memory: { status: 'healthy', usage: '72%' },
      lastBackup: new Date(Date.now() - 12 * 60 * 60 * 1000) // 12 hours ago
    };
  }
} 